'use client'
import { motion } from 'framer-motion'
import React, { useState } from 'react'
import Image from '@/components/ui/MediaImage'
import Link from 'next/link'
import { 
    tourCardVariants, 
    tourContentVariants,
    tourOverlaySmoothVariants, 
    tourButtonSmoothVariants 
} from '@/lib/animations';
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext"
import type { PageUrlMap } from "@/types/blog";
import { buildProductDetailUrl } from "@/lib/url-helpers";

export interface YachtRouteProps {
    id: number | string;
    src: string[];
    alt: string[];
    imageCount: number;
    videoCount: number;
    title: string;
    routeName?: string;
    startLocation?: string;
    endLocation?: string;
    durationHours?: number;
    durationDays?: number;
    routeType?: string;
    routeHighlights?: string;
    /** Kısa açıklama (kartlarda gösterilir) */
    shortDescription?: string;
    slug?: string;
    url?: string;
    is_featured?: boolean;
}

export interface YachtRouteCardProps {
    route: YachtRouteProps;
    viewType?: 'box' | 'horizontal';
    index?: number;
    isInView?: boolean;
    languageId?: number;
    language?: string;
    languageCode?: string;
    pageUrls?: PageUrlMap;
    defaultLanguage?: string;
}

// URL normalize fonksiyonu
const normalizeProductUrl = (
    url: string | undefined,
    productType: string,
    productSlug: string | undefined,
    pageUrls?: PageUrlMap,
    language?: string,
    defaultLanguage: string = 'en'
): string => {
    if (productSlug && pageUrls && language) {
        const correctUrl = buildProductDetailUrl(productType, productSlug, pageUrls, language, defaultLanguage);
        
        if (url) {
            const normalizedUrl = url.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
            
            if (normalizedUrl.startsWith(`/${productType}/`)) {
                return correctUrl;
            }
            
            if (normalizedUrl === correctUrl || normalizedUrl.startsWith(correctUrl.split('/').slice(0, -1).join('/'))) {
                return normalizedUrl;
            }
        }
        
        return correctUrl;
    }
    
    if (url) {
        const normalizedUrl = url.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
        if (normalizedUrl !== '/' && normalizedUrl !== '#') {
            return normalizedUrl;
        }
    }
    
    if (productSlug) {
        const langPrefix = language && language !== defaultLanguage ? `/${language}` : '';
        return `${langPrefix}/${productType}/${productSlug}`;
    }
    
    return '#';
};

export default function YachtRouteCard({ 
    route, 
    viewType = 'box', 
    index = 0,
    isInView = true,
    languageId,
    language,
    languageCode,
    pageUrls,
    defaultLanguage = 'en'
}: YachtRouteCardProps) {
    const { getVariable } = useLanguageVariables()
    const [isHovered, setIsHovered] = useState(false)
    const [imagesLoaded, setImagesLoaded] = useState<Record<number, boolean>>({})

    const rootInViewProps = { animate: 'visible' as const }
    const contentInViewProps = { initial: 'hidden' as const, animate: 'visible' as const }

    // Language code'u belirle
    const resolvedLanguageCode = language || languageCode || (typeof window !== 'undefined' ? (() => {
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const possibleLang = pathParts[0];
        if (possibleLang && possibleLang.length === 2 && /^[a-z]{2}$/.test(possibleLang)) {
            return possibleLang;
        }
        const cookieLang = document.cookie
            .split('; ')
            .find(row => row.startsWith('language='))
            ?.split('=')[1];
        return cookieLang || 'en';
    })() : 'en');

    // Product URL'ini oluştur
    const productUrl = normalizeProductUrl(
        route.url, 
        'yacht_routes', 
        route.slug, 
        pageUrls,
        resolvedLanguageCode,
        defaultLanguage
    );

    // Display name
    const displayName = route.routeName || route.title;

    // Duration text
    const durationText = (() => {
        const parts: string[] = [];
        if (route.durationDays !== undefined && route.durationDays > 0) {
            parts.push(`${route.durationDays} ${route.durationDays === 1 ? getVariable('yacht_route_card_day', 'Day') : getVariable('yacht_route_card_days', 'Days')}`);
        }
        if (route.durationHours !== undefined && route.durationHours > 0) {
            parts.push(`${route.durationHours} ${route.durationHours === 1 ? getVariable('yacht_route_card_hour', 'Hour') : getVariable('yacht_route_card_hours', 'Hours')}`);
        }
        return parts.length > 0 ? parts.join(' ') : null;
    })();

    return (
        <div className={`relative font-sans flex group cursor-pointer border border-neutral-200/90 ${route.is_featured ? 'shadow-tour-card-featured rounded-lg' : 'shadow-tour-card'} ${viewType == 'box' ? `flex-col rounded-tr-lg` : `flex-row bg-white rounded-lg p-4`}`} 
            onMouseEnter={() => setIsHovered(true)} 
            onMouseLeave={() => setIsHovered(false)}>
            <div className={`relative overflow-hidden h-[250px] ${viewType == 'box' ? 'w-full rounded-tl-lg rounded-tr-lg' : 'w-4/12 shrink-0 rounded-lg'}`}>
                {/* Loading skeleton */}
                {!imagesLoaded[0] && (
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse z-0" />
                )}
                
                {/* Image */}
                {route.src && route.src.length > 0 ? (
                    <Image
                        src={route.src[0]}
                        alt={route.alt[0] || displayName}
                        fill
                        className={`object-cover h-[250px] transition-opacity duration-300 ${viewType == 'box' ? 'rounded-tl-lg rounded-tr-lg' : 'rounded-lg'} ${imagesLoaded[0] ? 'opacity-100' : 'opacity-0'}`}
                        sizes={viewType === 'box' ? "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" : "33vw"}
                        loading={index === 0 ? "eager" : "lazy"}
                        unoptimized={route.src[0].startsWith('/api/media/proxy/')}
                        onLoad={() => {
                            setImagesLoaded(prev => ({ ...prev, [0]: true }))
                        }}
                        onError={() => {
                            setImagesLoaded(prev => ({ ...prev, [0]: true }))
                        }}
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-alsero-first/20 to-alsero-second/20 flex items-center justify-center">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" className="text-alsero-light-gray">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
                        </svg>
                    </div>
                )}

                {/* Overlay - Box view */}
                {viewType === 'box' && (
                    <motion.div
                        className={`w-full h-[250px] absolute top-0 z-10 pointer-events-auto`}
                        initial="hidden"
                        animate={isHovered ? "visible" : "hidden"}
                        variants={tourOverlaySmoothVariants}
                    >
                        <div className="flex flex-col justify-between h-full">
                            <div className="w-full flex flex-col gap-2 h-full items-end p-4 font-medium text-sm text-white">
                                <div className="flex flex-row gap-2">
                                    {route.imageCount > 0 && (
                                        <motion.div
                                            initial="hidden"
                                            animate={isHovered ? "visible" : "hidden"}
                                            variants={tourButtonSmoothVariants(isHovered ? 0.1 : 0)}
                                            className="flex flex-row gap-2 items-center bg-black/70 px-3 rounded-lg py-2 backdrop-blur-md hover:bg-black/80 transition-all duration-300">
                                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9.00156 11.3996C10.327 11.3996 11.4016 10.3251 11.4016 8.99961C11.4016 7.67413 10.327 6.59961 9.00156 6.59961C7.67608 6.59961 6.60156 7.67413 6.60156 8.99961C6.60156 10.3251 7.67608 11.3996 9.00156 11.3996Z" fill="white"/>
                                                <path d="M6.75 1.5L5.3775 3H3C2.175 3 1.5 3.675 1.5 4.5V13.5C1.5 14.325 2.175 15 3 15H15C15.825 15 16.5 14.325 16.5 13.5V4.5C16.5 3.675 15.825 3 15 3H12.6225L11.25 1.5H6.75ZM9 12.75C6.93 12.75 5.25 11.07 5.25 9C5.25 6.93 6.93 5.25 9 5.25C11.07 5.25 12.75 6.93 12.75 9C12.75 11.07 11.07 12.75 9 12.75Z" fill="white"/>
                                            </svg>
                                            <span>{route.imageCount}</span>
                                        </motion.div>
                                    )}
                                    {(route.videoCount !== undefined && route.videoCount !== null && Number(route.videoCount) > 0) && (
                                        <motion.div
                                            initial="hidden"
                                            animate={isHovered ? "visible" : "hidden"}
                                            variants={tourButtonSmoothVariants(isHovered ? 0.2 : 0)}
                                            className="flex flex-row gap-2 items-center bg-black/70 px-3 rounded-lg py-2 backdrop-blur-md hover:bg-black/80 transition-all duration-300">
                                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M2.02342 3.3408H0.703125V12.5829C0.703125 13.3091 1.29726 13.9032 2.02342 13.9032H11.2655V12.5829H2.02342V3.3408ZM12.5858 0.700195H4.66402C3.93786 0.700195 3.34372 1.29433 3.34372 2.0205V9.94229C3.34372 10.6685 3.93786 11.2626 4.66402 11.2626H12.5858C13.312 11.2626 13.9061 10.6685 13.9061 9.94229V2.0205C13.9061 1.29433 13.312 0.700195 12.5858 0.700195ZM7.30462 8.95207V3.01072L11.2655 5.98139L7.30462 8.95207Z" fill="white"/>
                                            </svg>
                                            <span>{route.videoCount}</span>
                                        </motion.div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>

            <div className="w-full">
                <motion.div className={`relative flex flex-col justify-between ${viewType == 'box' ? 'px-5 pt-10 pb-5 bg-white shadow-tour-card border border-neutral-200/80 -mt-2.5 group-hover:shadow-2xl z-50 transition-shadow duration-500 rounded-lg' : 'w-full h-full py-2.5 px-6'}`}>
                    
                    {/* Featured Badge */}
                    {viewType == 'box' && route.is_featured && (
                        <motion.span 
                            className={`absolute left-4 -top-[15px] bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-lg px-4 py-2 flex items-center gap-1.5 shadow-lg hover:shadow-xl transition-all duration-300 z-10 font-semibold text-sm`}
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: index ? index * 0.05 : 0 }}
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 0L8.5716 5.14286L14 5.14286L9.7142 8.28571L11.2858 13.4286L7 10.2857L2.7142 13.4286L4.2858 8.28571L0 5.14286L5.4284 5.14286L7 0Z" fill="white"/>
                            </svg>
                            {getVariable('yacht_route_card_featured', 'Featured')}
                        </motion.span>
                    )}

                    {/* Title */}
                    {viewType == 'box' ? (
                        <motion.h3 variants={tourContentVariants(index)} {...contentInViewProps} className="text-xl font-bold text-start text-alsero-second mb-2 min-h-[55px] line-clamp-2">
                            {displayName}
                        </motion.h3>
                    ) : (
                        <div className="flex flex-row justify-between items-center">
                            <motion.h3 variants={tourContentVariants(index)} {...contentInViewProps} className="text-lg font-bold text-start text-alsero-second mb-2 line-clamp-2">
                                {displayName}
                            </motion.h3>
                            {route.imageCount > 0 && (
                                <div className="flex flex-row gap-2">
                                    <motion.div className="flex flex-row gap-2 items-center bg-alsero-first px-3 py-2 text-white rounded">
                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M9.00156 11.3996C10.327 11.3996 11.4016 10.3251 11.4016 8.99961C11.4016 7.67413 10.327 6.59961 9.00156 6.59961C7.67608 6.59961 6.60156 7.67413 6.60156 8.99961C6.60156 10.3251 7.67608 11.3996 9.00156 11.3996Z" fill="white"/>
                                            <path d="M6.75 1.5L5.3775 3H3C2.175 3 1.5 3.675 1.5 4.5V13.5C1.5 14.325 2.175 15 3 15H15C15.825 15 16.5 14.325 16.5 13.5V4.5C16.5 3.675 15.825 3 15 3H12.6225L11.25 1.5H6.75ZM9 12.75C6.93 12.75 5.25 11.07 5.25 9C5.25 6.93 6.93 5.25 9 5.25C11.07 5.25 12.75 6.93 12.75 9C12.75 11.07 11.07 12.75 9 12.75Z" fill="white"/>
                                        </svg>
                                        <span>{route.imageCount}</span>
                                    </motion.div>
                                </div>
                            )}
                        </div>
                    )}

                    <motion.div
                        variants={tourContentVariants(index)}
                        {...contentInViewProps}
                        className="space-y-3 flex flex-col"
                    >
                        <div className={`text-sm text-alsero-light-gray ${viewType == 'box' ? 'flex flex-wrap items-center gap-3' : 'flex items-center gap-3 flex-wrap'}`}>
                            {/* Duration */}
                            {durationText && (
                                <span className="flex items-center gap-1">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm.5-9H7v4l3.5 2.1.5-.8-3-1.8V5z" fill="currentColor"/>
                                    </svg>
                                    {durationText}
                                </span>
                            )}

                            {/* Route Type */}
                            {route.routeType && (
                                <span className="flex items-center gap-1">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
                                    </svg>
                                    {route.routeType}
                                </span>
                            )}
                        </div>

                        {/* Start/End Location */}
                        {(route.startLocation || route.endLocation) && (
                            <div className="text-sm text-alsero-light-gray flex items-center gap-2">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z" fill="currentColor"/>
                                    <path d="M8 4c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" fill="currentColor"/>
                                </svg>
                                <span>
                                    {route.startLocation && route.endLocation 
                                        ? `${route.startLocation} → ${route.endLocation}`
                                        : route.startLocation || route.endLocation
                                    }
                                </span>
                            </div>
                        )}

                        {/* Kısa açıklama / Highlights */}
                        {(route.shortDescription || route.routeHighlights) && viewType === 'box' && (
                            <p className="text-sm text-alsero-dark-gray line-clamp-2">
                                {route.shortDescription || route.routeHighlights}
                            </p>
                        )}
                    </motion.div>

                    {/* View Details Button */}
                    <motion.div
                        variants={tourContentVariants(index)}
                        {...contentInViewProps}
                        className={`${viewType == 'box' ? 'mt-4' : 'justify-end'} flex flex-row pt-3 border-t border-alsero-light-gray/20`}
                    >
                        <Link href={productUrl} className="w-full">
                            <button
                                type="button"
                                className={`w-full ${viewType == 'box' ? 'py-3 px-4' : 'py-2.5 px-4'} bg-gradient-to-r from-alsero-first to-alsero-first/90 hover:from-alsero-first/90 hover:to-alsero-first/80 text-white rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2`}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm1 13H7v-2h2v2zm0-3H7V5h2v5z" fill="currentColor"/>
                                </svg>
                                <span>{getVariable('yacht_route_card_view_details', 'View Details')}</span>
                            </button>
                        </Link>
                    </motion.div>
                </motion.div>
            </div>

            {/* Clickable overlay */}
            <Link href={productUrl} className="absolute inset-0 z-0" aria-label={displayName} />
        </div>
    )
}

